import PropTypes from 'prop-types';
import { Alert, Box, Stack, Typography } from '@mui/material';
import Iconify from '@/components/iconify';
import { passwordErrors } from '@/consts/auth';
import { validatePasswordErrors } from '@/utils/validate';

const REQUIREMENTS = Object.values(passwordErrors);

const STATUS_STYLE = {
  met: { icon: 'lets-icons:check-fill', color: 'success.main' },
  failed: { icon: 'mdi:close-circle', color: 'error.main' },
  neutral: { icon: 'material-symbols:circle', color: 'text.disabled' },
};

PasswordRequirements.propTypes = {
  value: PropTypes.string,
  serverErrors: PropTypes.arrayOf(PropTypes.string),
  showErrors: PropTypes.bool,
};

export default function PasswordRequirements({ value = '', serverErrors = [], showErrors = false }) {
  const localErrors = validatePasswordErrors(value);
  const unmet = localErrors.map((error) => Object.values(error)[0]);

  const getStatus = (requirement) => {
    if (serverErrors.includes(requirement)) return 'failed';
    if (!unmet.includes(requirement)) return 'met';
    return showErrors ? 'failed' : 'neutral';
  };

  const maxLengthError = localErrors.find((error) => error.max)?.max;
  const alerts = [
    ...new Set(
      [maxLengthError, ...serverErrors].filter((message) => message && !REQUIREMENTS.includes(message))
    ),
  ];

  return (
    <Stack spacing={2}>
      <Box display="flex" flexDirection="column" gap={1}>
        {REQUIREMENTS.map((requirement) => {
          const status = getStatus(requirement);
          const { icon, color } = STATUS_STYLE[status];

          return (
            <Box
              key={requirement}
              display="flex"
              alignItems="center"
              data-testid="password-requirement"
              data-status={status}
            >
              <Iconify icon={icon} sx={{ color, width: 20 }} />
              <Typography variant="caption" sx={{ ml: 1, color }}>
                {requirement}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {alerts.map((message) => (
        <Alert key={message} severity="error">
          {message}
        </Alert>
      ))}
    </Stack>
  );
}
